import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { CoinMark } from "@/components/brand/Logo";
import AppShell from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, landingPath, useAuth } from "@/context/AuthContext";
import { HouseholdProvider } from "@/context/HouseholdContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Admin from "@/pages/Admin";
import Assistant from "@/pages/Assistant";
import AssetDetail from "@/pages/AssetDetail";
import Brand from "@/pages/Brand";
import Business from "@/pages/Business";
import ConsumptionDetail from "@/pages/ConsumptionDetail";
import Dashboard from "@/pages/Dashboard";
import Finances from "@/pages/Finances";
import Foyer from "@/pages/Foyer";
import Login from "@/pages/Login";
import Market from "@/pages/Market";
import Onboarding from "@/pages/Onboarding";
import PartnerRegister from "@/pages/PartnerRegister";
import Partners from "@/pages/Partners";
import PaymentSuccess from "@/pages/PaymentSuccess";
import FriendInvite from "@/pages/FriendInvite";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import Professionals from "@/pages/Professionals";
import { UpdateBanner } from "@/components/common/UpdateBanner";
import Friends from "@/pages/Friends";
import OnlineAccount from "@/pages/OnlineAccount";
import Pots from "@/pages/Pots";
import PotDetail from "@/pages/PotDetail";
import PublicPot from "@/pages/PublicPot";
import Setup from "@/pages/Setup";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import ProPortal from "@/pages/ProPortal";
import Register from "@/pages/Register";
import Settings from "@/pages/Settings";

const Splash = () => (
  <div className="flex min-h-screen items-center justify-center bg-background" data-testid="app-splash">
    <CoinMark size={72} className="coin-float" />
  </div>
);

const Protected = ({ children }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <Splash />;
  if (!user) return pathname === "/" ? <Landing /> : <Navigate to="/login" replace />;
  if (user.onboarding_completed === false && pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={landingPath(user)} replace />;
  return children;
};

const Personal = ({ children }) => {
  const { user } = useAuth();
  if (!user.households.length && (user.organizations.length || user.platform_role || user.companies?.length || user.partner)) return <Navigate to={landingPath(user)} replace />;
  return children;
};

const ProOnly = ({ children }) => {
  const { user } = useAuth();
  return user.organizations.length ? children : <Navigate to="/" replace />;
};

const SuperAdmin = ({ children }) => {
  const { user } = useAuth();
  return ["admin", "demo_admin"].includes(user.platform_role) ? children : <Navigate to={landingPath(user)} replace />;
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <HouseholdProvider>
            <Routes>
              <Route path="/c/:token" element={<PublicPot />} />
              <Route path="/legal/:slug" element={<Legal />} />
              <Route path="/professionnels" element={<Professionals />} />
              <Route path="/ami/:token" element={<FriendInvite />} />
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
              <Route path="/partenaires/inscription" element={<PublicOnly><PartnerRegister /></PublicOnly>} />
              <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
              <Route path="/configuration" element={<Protected><Setup /></Protected>} />
              <Route element={<Protected><AppShell /></Protected>}>
                <Route index element={<Dashboard />} />
                <Route path="finances" element={<Personal><Finances /></Personal>} />
                <Route path="foyer" element={<Personal><Foyer /></Personal>} />
                <Route path="market" element={<Personal><Market /></Personal>} />
                <Route path="cagnottes" element={<Pots />} />
                <Route path="cagnottes/:id" element={<PotDetail />} />
                <Route path="proches" element={<Friends />} />
                <Route path="compte-en-ligne" element={<OnlineAccount />} />
                <Route path="assistant" element={<Personal><Assistant /></Personal>} />
                <Route path="settings" element={<Settings />} />
                <Route path="pro" element={<ProOnly><ProPortal /></ProOnly>} />
                <Route path="pro/:section" element={<ProOnly><ProPortal /></ProOnly>} />
                <Route path="partenaires" element={<Partners />} />
                <Route path="partenaires/:section" element={<Partners />} />
                <Route path="payment/success" element={<PaymentSuccess />} />
                <Route path="entreprise" element={<Business />} />
                <Route path="entreprise/:section" element={<Business />} />
                <Route path="admin" element={<Admin />} />
                <Route path="admin/:section" element={<Admin />} />
                <Route path="brand" element={<SuperAdmin><Brand /></SuperAdmin>} />
                <Route path="foyer/consommation/:kind" element={<Personal><ConsumptionDetail /></Personal>} />
                <Route path="foyer/equipements/:aid" element={<Personal><AssetDetail /></Personal>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <UpdateBanner />
            <Toaster position="top-center" richColors />
          </HouseholdProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
